/* ==========================================================================
   Sevine Dumont: shared helpers (vanilla, no dependencies)
   Owner: architect. Docs: snippets/sd-README.md
   Custom elements: <sd-slider>, <sd-card>, <sd-delivery>, <sd-timer>, <sd-tabs>
   Global: window.SD (formatMoney, delivery, storage, reveal)
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG = window.SD_CONFIG || {};
  var SD = (window.SD = window.SD || {});
  SD.config = CONFIG;

  /* ---------- small utils ---------- */
  SD.storage = {
    get: function (store, key) {
      try { return window[store].getItem(key); } catch (e) { return null; }
    },
    set: function (store, key, value) {
      try { window[store].setItem(key, value); } catch (e) { /* storage blocked */ }
    },
    remove: function (store, key) {
      try { window[store].removeItem(key); } catch (e) { /* storage blocked */ }
    }
  };

  SD.prefersReducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  SD.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || CONFIG.moneyFormat || '${{amount}}';
    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    if (cents % 100 === 0 && CONFIG.moneyTrimZeros !== false) value = value.replace(/[.,]00$/, '');
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /* ==========================================================================
     Delivery date + countdown (Eastern Time, business days)
     ========================================================================== */
  var DELIVERY_DEFAULTS = {
    processingDays: 1,
    transitMin: 3,
    transitMax: 5,
    cutoff: '14:00',
    timeZone: 'America/New_York',
    skipWeekends: true,
    holidays: []
  };

  function nowInZone(timeZone) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone, year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23'
    });
    var parts = {};
    fmt.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    return {
      date: Date.UTC(+parts.year, +parts.month - 1, +parts.day),
      minutes: (+parts.hour % 24) * 60 + (+parts.minute) + (+parts.second) / 60
    };
  }

  var DAY = 86400000;

  function isoDay(utc) { return new Date(utc).toISOString().slice(0, 10); }

  function isBusinessDay(utc, opts) {
    var dow = new Date(utc).getUTCDay();
    if (opts.skipWeekends && (dow === 0 || dow === 6)) return false;
    return opts.holidays.indexOf(isoDay(utc)) === -1;
  }

  function addBusinessDays(utc, n, opts) {
    var d = utc;
    var added = 0;
    while (added < n) {
      d += DAY;
      if (isBusinessDay(d, opts)) added++;
    }
    return d;
  }

  function parseCutoff(value) {
    var m = String(value || '14:00').match(/^(\d{1,2})(?::(\d{2}))?/);
    return m ? (+m[1]) * 60 + (+(m[2] || 0)) : 840;
  }

  SD.delivery = {
    defaults: DELIVERY_DEFAULTS,
    /* Returns { orderDay, shipDay, min, max, minutesLeft } as UTC midnight timestamps */
    compute: function (options) {
      var opts = Object.assign({}, DELIVERY_DEFAULTS, CONFIG.delivery || {}, options || {});
      if (typeof opts.holidays === 'string') opts.holidays = opts.holidays.split(/[\s,]+/).filter(Boolean);
      var now = nowInZone(opts.timeZone);
      var cutoff = parseCutoff(opts.cutoff);
      var orderDay = now.date;
      if (!isBusinessDay(orderDay, opts) || now.minutes >= cutoff) {
        orderDay = addBusinessDays(orderDay, 1, opts);
      }
      var shipDay = addBusinessDays(orderDay, +opts.processingDays || 0, opts);
      var min = addBusinessDays(shipDay, +opts.transitMin || 0, opts);
      var max = addBusinessDays(shipDay, Math.max(+opts.transitMax || 0, +opts.transitMin || 0), opts);
      var minutesLeft = Math.round((orderDay - now.date) / 60000) + cutoff - now.minutes;
      return { orderDay: orderDay, shipDay: shipDay, min: min, max: max, minutesLeft: Math.max(0, minutesLeft) };
    },
    formatDate: function (utc, style) {
      var d = new Date(utc);
      if (style === 'day-month') {
        return String(d.getUTCDate()).padStart(2, '0') + ' ' + d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      }
      if (style === 'long') {
        return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
      }
      if (style === 'month-day') {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      }
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
    },
    formatCountdown: function (minutes) {
      minutes = Math.max(0, Math.floor(minutes));
      var h = Math.floor(minutes / 60);
      var m = minutes % 60;
      var hs = h === 1 ? ' hr ' : ' hrs ';
      var ms = m === 1 ? ' min' : ' mins';
      return h > 0 ? h + hs + m + ms : m + ms;
    }
  };

  class SdDelivery extends HTMLElement {
    connectedCallback() {
      this.render();
      this._interval = window.setInterval(this.render.bind(this), 30000);
    }
    disconnectedCallback() {
      window.clearInterval(this._interval);
    }
    options() {
      var ds = this.dataset;
      var o = {};
      if (ds.processingDays !== undefined && ds.processingDays !== '') o.processingDays = +ds.processingDays;
      if (ds.transitMin !== undefined && ds.transitMin !== '') o.transitMin = +ds.transitMin;
      if (ds.transitMax !== undefined && ds.transitMax !== '') o.transitMax = +ds.transitMax;
      if (ds.cutoff) o.cutoff = ds.cutoff;
      if (ds.timeZone) o.timeZone = ds.timeZone;
      if (ds.holidays) o.holidays = ds.holidays;
      if (ds.skipWeekends) o.skipWeekends = ds.skipWeekends !== 'false';
      return o;
    }
    render() {
      var r;
      try { r = SD.delivery.compute(this.options()); } catch (e) { return; }
      var style = this.dataset.dateFormat || 'short';
      var set = function (sel, text) {
        this.querySelectorAll(sel).forEach(function (el) { el.textContent = text; });
      }.bind(this);
      set('[data-sd-countdown]', SD.delivery.formatCountdown(r.minutesLeft));
      set('[data-sd-date]', SD.delivery.formatDate(r.min, style));
      set('[data-sd-date-max]', SD.delivery.formatDate(r.max, style));
      set('[data-sd-ship-date]', SD.delivery.formatDate(r.shipDay, style));
      set('[data-sd-date-range]', r.min === r.max
        ? SD.delivery.formatDate(r.min, style)
        : SD.delivery.formatDate(r.min, style) + ' – ' + SD.delivery.formatDate(r.max, style));
      this.setAttribute('data-sd-ready', '');
      this.dispatchEvent(new CustomEvent('sd:delivery:update', { bubbles: true, detail: r }));
    }
  }

  /* ==========================================================================
     Reservation timer ("Your bag is reserved for 15:00")
     ========================================================================== */
  class SdTimer extends HTMLElement {
    connectedCallback() {
      this.minutes = +this.dataset.minutes || +(CONFIG.reserveMinutes || 15);
      this.key = 'sd-timer-' + (this.dataset.key || 'cart');
      var start = +SD.storage.get('sessionStorage', this.key);
      if (!start) {
        start = Date.now();
        SD.storage.set('sessionStorage', this.key, String(start));
      }
      this.start = start;
      this.tick();
      this._interval = window.setInterval(this.tick.bind(this), 1000);
    }
    disconnectedCallback() {
      window.clearInterval(this._interval);
    }
    reset() {
      this.start = Date.now();
      SD.storage.set('sessionStorage', this.key, String(this.start));
      this.removeAttribute('data-expired');
      this.tick();
    }
    tick() {
      var left = Math.max(0, Math.round(this.minutes * 60 - (Date.now() - this.start) / 1000));
      var mm = String(Math.floor(left / 60)).padStart(2, '0');
      var ss = String(left % 60).padStart(2, '0');
      this.querySelectorAll('[data-sd-timer-display]').forEach(function (el) { el.textContent = mm + ':' + ss; });
      if (left === 0 && !this.hasAttribute('data-expired')) {
        if (this.dataset.restart === 'true') { this.reset(); return; }
        this.setAttribute('data-expired', '');
        this.querySelectorAll('[data-sd-timer-active]').forEach(function (el) { el.hidden = true; });
        this.querySelectorAll('[data-sd-timer-expired]').forEach(function (el) { el.hidden = false; });
        this.dispatchEvent(new CustomEvent('sd:timer:expired', { bubbles: true }));
      }
    }
  }

  /* ==========================================================================
     Slider (CSS scroll-snap track + optional arrows, progress, dots)
     ========================================================================== */
  class SdSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sd-slider-track]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-sd-slider-prev]');
      this.nextBtn = this.querySelector('[data-sd-slider-next]');
      this.progress = this.querySelector('[data-sd-slider-progress]');
      this.dots = this.querySelector('[data-sd-slider-dots]');
      this.counter = this.querySelector('[data-sd-slider-counter]');
      this._onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.prev.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.next.bind(this));
      this.track.addEventListener('keydown', this.onKey.bind(this));
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(this.update.bind(this));
        this._ro.observe(this.track);
      } else {
        window.addEventListener('resize', this.update.bind(this));
      }
      this.update();
    }
    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);
    }
    get slides() {
      return Array.prototype.filter.call(this.track.children, function (el) { return el.offsetParent !== null || el.offsetWidth > 0; });
    }
    pageSize() {
      var slides = this.slides;
      if (!slides.length) return 1;
      var w = slides[0].getBoundingClientRect().width || 1;
      return Math.max(1, Math.floor((this.track.clientWidth + 2) / w));
    }
    pageCount() {
      return Math.max(1, Math.ceil(this.slides.length / this.pageSize()));
    }
    currentIndex() {
      var slides = this.slides;
      var trackLeft = this.track.getBoundingClientRect().left + (parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0);
      var best = 0;
      var bestDist = Infinity;
      slides.forEach(function (s, i) {
        var d = Math.abs(s.getBoundingClientRect().left - trackLeft);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }
    goTo(index) {
      var slides = this.slides;
      if (!slides.length) return;
      index = Math.max(0, Math.min(index, slides.length - 1));
      var pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      var left = slides[index].getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft - pad;
      this.track.scrollTo({ left: left, behavior: SD.prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    next() { this.goTo(this.currentIndex() + this.pageSize()); }
    prev() { this.goTo(this.currentIndex() - this.pageSize()); }
    onKey(e) {
      if (e.target !== this.track) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
    }
    onScroll() {
      if (this._raf) return;
      this._raf = window.requestAnimationFrame(function () {
        this._raf = null;
        this.update();
      }.bind(this));
    }
    buildDots() {
      if (!this.dots) return;
      var pages = this.pageCount();
      if (this.dots.childElementCount === pages) return;
      this.dots.innerHTML = '';
      var label = this.dataset.dotLabel || 'Go to slide';
      for (var i = 0; i < pages; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sd-slider__dot';
        b.setAttribute('aria-label', label + ' ' + (i + 1));
        b.addEventListener('click', this.goTo.bind(this, i * this.pageSize()));
        this.dots.appendChild(b);
      }
    }
    update() {
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      var overflow = max > 2;
      this.toggleAttribute('data-sd-no-overflow', !overflow);
      if (this.prevBtn) this.prevBtn.disabled = t.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = t.scrollLeft >= max - 2;
      if (this.progress) {
        var size = t.scrollWidth ? Math.min(100, (t.clientWidth / t.scrollWidth) * 100) : 100;
        var ratio = max > 0 ? t.scrollLeft / max : 0;
        this.progress.style.setProperty('--sd-progress-size', size + '%');
        this.progress.style.setProperty('--sd-progress-offset', ((100 / size) * 100 - 100) * ratio + '%');
      }
      var index = this.currentIndex();
      var page = Math.min(this.pageCount() - 1, Math.round(index / this.pageSize()));
      if (t.scrollLeft >= max - 2 && overflow) page = this.pageCount() - 1;
      this.buildDots();
      if (this.dots) {
        Array.prototype.forEach.call(this.dots.children, function (d, i) {
          d.setAttribute('aria-current', i === page ? 'true' : 'false');
        });
      }
      if (this.counter) this.counter.textContent = (page + 1) + ' / ' + this.pageCount();
      if (index !== this._lastIndex) {
        this._lastIndex = index;
        this.dispatchEvent(new CustomEvent('sd:slider:change', { bubbles: true, detail: { index: index, page: page } }));
      }
    }
  }

  /* ==========================================================================
     Product card: swipeable colour images, swatches, pips, arrows
     ========================================================================== */
  class SdCard extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sd-card-track]');
      if (!this.track) return;
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('.sd-card__slide'));
      this.pips = Array.prototype.slice.call(this.querySelectorAll('.sd-card__pip'));
      this.swatches = Array.prototype.slice.call(this.querySelectorAll('[data-sd-swatch]'));
      this.links = Array.prototype.slice.call(this.querySelectorAll('[data-sd-card-link]'));
      this.prevBtn = this.querySelector('[data-sd-card-prev]');
      this.nextBtn = this.querySelector('[data-sd-card-next]');
      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.swatches.forEach(function (s) {
        s.addEventListener('click', this.onSwatch.bind(this, s));
        if (window.matchMedia('(hover: hover)').matches) {
          s.addEventListener('mouseenter', this.onSwatch.bind(this, s));
        }
      }.bind(this));
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { this.goTo(this.index() - 1); }.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { this.goTo(this.index() + 1); }.bind(this));
      this.sync();
    }
    index() {
      var w = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / w);
    }
    goTo(i) {
      if (!this.slides.length) return;
      i = Math.max(0, Math.min(i, this.slides.length - 1));
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: SD.prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    onScroll() {
      if (this._raf) return;
      this._raf = window.requestAnimationFrame(function () { this._raf = null; this.sync(); }.bind(this));
    }
    onSwatch(swatch) {
      var slide = parseInt(swatch.dataset.slide, 10);
      if (!isNaN(slide)) this.goTo(slide);
      this.setSwatch(swatch);
    }
    setSwatch(swatch) {
      this.swatches.forEach(function (s) { s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false'); });
      if (swatch && swatch.dataset.url) {
        this.links.forEach(function (a) { a.setAttribute('href', swatch.dataset.url); });
      }
    }
    sync() {
      var i = this.index();
      this.pips.forEach(function (p, n) { p.setAttribute('aria-current', n === i ? 'true' : 'false'); });
      if (this.prevBtn) this.prevBtn.disabled = i <= 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= this.slides.length - 1;
      var match = this.swatches.filter(function (s) { return parseInt(s.dataset.slide, 10) === i; })[0];
      if (match) this.setSwatch(match);
    }
  }

  /* ==========================================================================
     Tabs (WAI-ARIA tabs pattern)
     ========================================================================== */
  class SdTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', this.select.bind(this, i, false));
        tab.addEventListener('keydown', this.onKey.bind(this));
      }.bind(this));
      var start = this.tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; });
      this.select(start < 0 ? 0 : start, true);
    }
    select(i, silent) {
      this.tabs.forEach(function (tab, n) {
        var on = n === i;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        var panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (!silent) this.dispatchEvent(new CustomEvent('sd:tabs:change', { bubbles: true, detail: { index: i } }));
    }
    onKey(e) {
      var i = this.tabs.indexOf(e.currentTarget);
      var n = null;
      if (e.key === 'ArrowRight') n = (i + 1) % this.tabs.length;
      if (e.key === 'ArrowLeft') n = (i - 1 + this.tabs.length) % this.tabs.length;
      if (e.key === 'Home') n = 0;
      if (e.key === 'End') n = this.tabs.length - 1;
      if (n === null) return;
      e.preventDefault();
      this.select(n, false);
      this.tabs[n].focus();
    }
  }

  /* ==========================================================================
     Reveal on scroll (.sd-reveal -> .is-visible)
     ========================================================================== */
  var revealObserver = null;
  SD.reveal = function (root) {
    var els = (root || document).querySelectorAll('.sd-reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window) || SD.prefersReducedMotion()) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px 15% 0px', threshold: 0.05 });
    }
    els.forEach(function (el) { revealObserver.observe(el); });
  };

  function define(name, ctor) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  }

  define('sd-delivery', SdDelivery);
  define('sd-timer', SdTimer);
  define('sd-slider', SdSlider);
  define('sd-card', SdCard);
  define('sd-tabs', SdTabs);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { SD.reveal(); });
  } else {
    SD.reveal();
  }
  document.addEventListener('shopify:section:load', function (e) { SD.reveal(e.target); });
})();
